/**
 * noook play — Sticky Add to Cart (v2)
 * Rich sticky bar with variant pills, quantity stepper, live price.
 *
 * Mobile:  image (40px) + price + yellow ATC button (variants & qty hidden).
 * Desktop: image (52px) + title + variant pills + qty stepper + price + ATC.
 *
 * Syncs bidirectionally with noook play theme's <variant-selects> via PUB_SUB_EVENTS.
 * Uses IntersectionObserver on the main ATC button for show/hide.
 */

class StickyATC extends HTMLElement {
  connectedCallback() {
    this.mainForm = document.querySelector('product-form form');
    this.mainBuyButton = document.querySelector('.product-form__submit');
    this.sectionId = this.dataset.sectionId;

    // Cache DOM refs
    this.priceEl = this.querySelector('[data-sticky-price]');
    this.variantInput = this.querySelector('.sticky-atc__variant-id');
    this.qtyInput = this.querySelector('.sticky-atc__qty-input');
    this.formQtyInput = this.querySelector('.sticky-atc__form-qty');
    this.stickyForm = this.querySelector('.sticky-atc__form');
    this.atcButton = this.querySelector('.sticky-atc__button');

    // Parse variants JSON
    const variantsScript = this.querySelector('[data-sticky-variants]');
    this.variants = variantsScript ? JSON.parse(variantsScript.textContent) : [];

    // Track current selected option values
    this.selectedOptions = {};
    this._unsubscribers = [];
    this.querySelectorAll('.sticky-atc__pill--selected').forEach((pill) => {
      this.selectedOptions[pill.dataset.optionIndex] = pill.dataset.optionValue;
    });

    if (!this.mainBuyButton) return;

    this._initObserver();
    this._initVariantPills();
    this._initGiftCheckbox();
    this._initQuantity();
    this._initFormSubmit();
    this._initPubSubSync();
  }

  /* -------------------------------------------------------
     IntersectionObserver — show/hide sticky bar
     ------------------------------------------------------- */
  _initObserver() {
    // Show sticky bar ONLY when the user has scrolled the main ATC button
    // out through the TOP of the viewport. Never show when the button is
    // simply below the fold (e.g. on mobile page load).
    //
    // Logic: check entry.boundingClientRect.bottom — if it's negative,
    // the button has scrolled above the viewport. If it's positive but
    // isIntersecting is false, the button is below the viewport.

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Button is visible — hide sticky bar
            this.hide();
          } else if (entry.boundingClientRect.bottom < 0) {
            // Button has scrolled above viewport — show sticky bar
            this.show();
          }
          // If button is below viewport (bottom > 0, not intersecting),
          // do nothing — keep sticky bar hidden
        });
      },
      { threshold: 0 }
    );
    this.observer.observe(this.mainBuyButton);
  }

  show() {
    this.classList.add('sticky-atc--visible');
    this.removeAttribute('aria-hidden');
    document.body.classList.add('has-sticky-atc');
  }

  hide() {
    this.classList.remove('sticky-atc--visible');
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('has-sticky-atc');
  }

  /* -------------------------------------------------------
     Variant pills — click handler → update main form
     ------------------------------------------------------- */
  _initVariantPills() {
    this.querySelectorAll('.sticky-atc__pill').forEach((pill) => {
      pill.addEventListener('click', (e) => this._onPillClick(e));
    });
  }

  /* -------------------------------------------------------
     Gift wrapping checkbox → update main form
     ------------------------------------------------------- */
  _initGiftCheckbox() {
    this.querySelectorAll('.sticky-atc__gift-checkbox').forEach((cb) => {
      cb.addEventListener('change', () => {
        const idx = cb.dataset.optionIndex;
        const value = cb.checked ? cb.dataset.onValue : cb.dataset.offValue;
        this.selectedOptions[idx] = value;
        this._syncToMainForm(idx, value);
      });
    });
  }

  _onPillClick(event) {
    const pill = event.currentTarget;
    const optionIndex = pill.dataset.optionIndex;
    const optionValue = pill.dataset.optionValue;

    // Update visual state
    const group = pill.closest('.sticky-atc__option-group');
    group.querySelectorAll('.sticky-atc__pill').forEach((p) => p.classList.remove('sticky-atc__pill--selected'));
    pill.classList.add('sticky-atc__pill--selected');

    // Track selection
    this.selectedOptions[optionIndex] = optionValue;

    // Sync to noook play theme's <variant-selects> by changing the <select> and firing change
    this._syncToMainForm(optionIndex, optionValue);
  }

  _syncToMainForm(optionIndex, optionValue) {
    const variantSelects = document.querySelector(`variant-selects[data-section="${this.sectionId}"]`);
    if (!variantSelects) return;

    // noook play theme uses <select> elements or <input> radios inside fieldsets
    const selects = variantSelects.querySelectorAll('select.select__select');
    if (selects[optionIndex]) {
      const selectEl = selects[optionIndex];
      // Find the matching option
      const matchOption = Array.from(selectEl.options).find(
        (opt) => opt.value === optionValue
      );
      if (matchOption) {
        selectEl.value = optionValue;
        selectEl.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
    }

    // Fallback: try radio/button inputs inside fieldsets
    const fieldsets = variantSelects.querySelectorAll('fieldset');
    if (fieldsets[optionIndex]) {
      const input = fieldsets[optionIndex].querySelector(`input[value="${CSS.escape(optionValue)}"]`);
      if (input) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  /* -------------------------------------------------------
     PubSub — listen for variant changes from main form
     ------------------------------------------------------- */
  _initPubSubSync() {
    if (typeof subscribe !== 'function') return;

    this._unsubscribers.push(subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const variant = event.data.variant;
      if (!variant) {
        // Variant unavailable
        this._setUnavailable();
        return;
      }

      // Update variant ID
      if (this.variantInput) {
        this.variantInput.value = variant.id;
      }

      // Update price
      this._updatePrice(variant);

      // Update availability
      this._updateAvailability(variant);

      // Update pill selected states to match
      this._syncPillsFromVariant(variant);
    }));
  }

  _updatePrice(variant) {
    if (!this.priceEl) return;
    const formatted = this._formatMoney(variant.price);
    this.priceEl.textContent = formatted;
  }

  _formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents);
    }
    // Fallback: format with currency symbol from the page
    const currencySymbol = document.querySelector('[data-currency-symbol]')?.textContent?.trim() || '£';
    return currencySymbol + (cents / 100).toFixed(2);
  }

  _updateAvailability(variant) {
    if (!this.atcButton) return;
    if (variant.available) {
      this.atcButton.disabled = false;
      this._setLabel(this.atcButton.dataset.addText || this._getLabel());
    } else {
      this.atcButton.disabled = true;
      this._setLabel(window.variantStrings?.soldOut || 'Sold Out');
    }
  }

  _setUnavailable() {
    if (!this.atcButton) return;
    this.atcButton.disabled = true;
    this._setLabel(window.variantStrings?.unavailable || 'Unavailable');
  }

  _syncPillsFromVariant(variant) {
    // variant.options is an array like ["Red", "Large"]
    if (!variant.options) return;
    variant.options.forEach((value, index) => {
      this.selectedOptions[index] = value;

      // Sync pill buttons
      const group = this.querySelector(`.sticky-atc__option-group[data-option-index="${index}"]`);
      if (group) {
        group.querySelectorAll('.sticky-atc__pill').forEach((pill) => {
          pill.classList.toggle(
            'sticky-atc__pill--selected',
            pill.dataset.optionValue === value
          );
        });
        return;
      }

      // Sync gift wrapping checkbox
      const cb = this.querySelector(`.sticky-atc__gift-checkbox[data-option-index="${index}"]`);
      if (cb) {
        cb.checked = (value === cb.dataset.onValue);
      }
    });
  }

  /* -------------------------------------------------------
     Quantity stepper
     ------------------------------------------------------- */
  _initQuantity() {
    this.querySelectorAll('.sticky-atc__qty-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const action = btn.dataset.action;
        let qty = parseInt(this.qtyInput.value, 10) || 1;

        if (action === 'increase') {
          qty = Math.min(qty + 1, 99);
        } else {
          qty = Math.max(qty - 1, 1);
        }

        this.qtyInput.value = qty;
        if (this.formQtyInput) {
          this.formQtyInput.value = qty;
        }
      });
    });
  }

  /* -------------------------------------------------------
     Form submission — proxy to main noook play theme form
     ------------------------------------------------------- */
  _initFormSubmit() {
    if (!this.stickyForm) return;

    this.stickyForm.addEventListener('submit', (e) => {
      // v2.17.2 ATC feedback — with no main form to proxy to, let the form submit natively
      if (!this.mainForm) return;
      e.preventDefault();
      if (this._isSubmitting) return;
      // The main form ignores submits while its own add is in flight; don't enter a loading
      // state that no feedback event would ever end.
      const mainSubmit = this.mainForm.querySelector('[type="submit"]');
      if (mainSubmit && mainSubmit.getAttribute('aria-disabled') === 'true') return;
      this._setSubmitting(true);
      // Safety net: never leave the sticky button locked if no feedback event arrives.
      window.clearTimeout(this._feedbackTimeout);
      this._feedbackTimeout = window.setTimeout(() => this._setSubmitting(false), 10000);

      {
        // Sync variant ID to main form
        const mainVariantInput = this.mainForm.querySelector('.product-variant-id, input[name="id"]');
        if (mainVariantInput && this.variantInput) {
          mainVariantInput.value = this.variantInput.value;
        }

        // Sync quantity to main form
        const mainQtyInput = this.mainForm.querySelector('input[name="quantity"]');
        if (mainQtyInput && this.qtyInput) {
          mainQtyInput.value = this.qtyInput.value;
          // noook play theme's quantity-input custom element may need a change event
          mainQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Submit the main form (triggers noook play theme's cart drawer / AJAX add)
        this.mainForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
    });

    document.addEventListener('atc:feedback-success', this._onATCSuccess = () => this._showSuccess());
    document.addEventListener('atc:feedback-error', this._onATCError = () => this._setSubmitting(false));
  }

  _setSubmitting(isSubmitting) {
    if (!this.atcButton) return;
    if (!isSubmitting) window.clearTimeout(this._feedbackTimeout);
    if (isSubmitting) {
      this._isSubmitting = true;
      this._atcOriginalText = this._getLabel();
      this.atcButton.disabled = true;
      this.atcButton.setAttribute('aria-busy', 'true');
      this.atcButton.style.minWidth = `${this.atcButton.getBoundingClientRect().width}px`;
      this.atcButton.classList.add('loading');
      this.atcButton.querySelector('.loading__spinner')?.classList.remove('hidden');
    } else if (this._isSubmitting) {
      this._isSubmitting = false;
      this.atcButton.disabled = false;
      this.atcButton.removeAttribute('aria-busy');
      this.atcButton.classList.remove('loading', 'atc-feedback--success');
      this.atcButton.style.minWidth = '';
      this.atcButton.querySelector('.loading__spinner')?.classList.add('hidden');
      const liveRegion = this.stickyForm?.querySelector('.atc-feedback__live');
      if (liveRegion) liveRegion.textContent = '';
      this._setLabel(this._atcOriginalText || this.atcButton.dataset.addText || this._getLabel());
    }
  }

  async _showSuccess() {
    if (!this._isSubmitting || !this.atcButton) return;
    this.atcButton.classList.remove('loading');
    this.atcButton.classList.add('atc-feedback--success');
    this._setLabel(this.atcButton.dataset.addedText || 'Added');
    const liveRegion = this.stickyForm?.querySelector('.atc-feedback__live');
    if (liveRegion) liveRegion.textContent = this.atcButton.dataset.addedAnnouncement || 'Added to cart';
    await new Promise((resolve) => window.setTimeout(resolve, 500));
    this._setSubmitting(false);
  }

  _getLabel() {
    return this.atcButton.querySelector('.atc-feedback__label')?.textContent.trim() || '';
  }

  _setLabel(text) {
    const label = this.atcButton.querySelector('.atc-feedback__label');
    if (label) label.textContent = text;
  }

  /* -------------------------------------------------------
     Cleanup
     ------------------------------------------------------- */
  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
    // Tear down PubSub subscriptions so a removed/re-rendered section (theme editor,
    // dynamic section swap) doesn't leave detached instances reacting to events. (2026-06-23 QC)
    if (this._unsubscribers) {
      this._unsubscribers.forEach((unsub) => typeof unsub === 'function' && unsub());
      this._unsubscribers = [];
    }
    document.removeEventListener('atc:feedback-success', this._onATCSuccess);
    document.removeEventListener('atc:feedback-error', this._onATCError);
  }
}

if (!customElements.get('sticky-atc')) {
  customElements.define('sticky-atc', StickyATC);
}
